import type { CSSResult } from 'lit'

import { unsafeCSS } from 'lit'

type UndescroreToDash<S extends string> =
	S extends `${infer Head}_${infer Tail}`
		? `${Head}-${UndescroreToDash<Tail>}`
		: S

/**
 * Create a set of local custom properties that are scoped to a component
 */
export function scoped<Prefix extends string, Vars extends readonly string[]>(
	prefix: Prefix,
	vars: Vars & (string[] extends Vars ? never : unknown)
): { [K in Vars[number]]: `--lfds--${Prefix}-${UndescroreToDash<K>}-local` } {
	return Object.fromEntries(
		vars.map((name) => [
			name,
			`--lfds--${prefix}-${name.replaceAll('_', '-')}-local`
		])
	) as { [K in Vars[number]]: `--lfds--${Prefix}-${UndescroreToDash<K>}-local` }
}

/**
 * Declare a set of local custom properties that are inherited from outside the
 * host element, optionally with defaults
 */
export function declare<
	Locals extends Record<string, string>,
	Defaults extends Partial<Record<keyof Locals, string>>
>(
	locals: Locals,
	defaults?: Defaults & Record<Exclude<keyof Defaults, keyof Locals>, never>
) {
	return unsafeCSS(
		Object.entries(locals)
			.map(
				([name, local]) =>
					`${local}: ${vars(local.replace(/-local$/, ''), defaults?.[name])};`
			)
			.join('\n')
	)
}

/**
 * Define a subset of another components custom properties, overriding their
 * local values
 */
export function define<
	Locals extends Record<string, string>,
	Values extends Partial<Record<keyof Locals, string | CSSResult>>
>(locals: Locals, values: Values) {
	return unsafeCSS(
		Object.entries(values)
			.map(
				([name, value]) => `${locals[name].replace(/-local$/, '')}: ${value};`
			)
			.join('\n')
	)
}

/**
 * Override a local custom property with new value(s)
 */
export function override(local: string, ...values: string[]) {
	const names = values.map((value) =>
		// Overriding with self means we want to use the default value
		value === local ? value.replace(/-local$/, '') : value
	)
	return unsafeCSS(`${local}: ${vars(...names)};`)
}

/**
 * Output a custom property, optionally with fallback(s)
 */
export function vars(...vars: string[]) {
	return unsafeCSS(
		vars.reduceRight((acc, name) => {
			if (name == null) return acc
			const value = acc ? `${name}, ${acc}` : name
			return name.startsWith('--') ? `var(${value})` : value
		}, '')
	)
}
